import React, { useState, useEffect } from 'react'
import { useLocation } from 'react-router-dom'
import { buildButtonClasses } from '@/components/UI/Button/Button'
import { usePricingPlans } from '@/hooks/usePricingPlans'
import ricaldLogo from '@/assets/ricald-logo.png'

export const ModernNavbar: React.FC = () => {
  const { marketingCopy } = usePricingPlans()
  const location = useLocation()
  const [isScrolled, setIsScrolled] = useState(false)
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false)

  const isLoginPage = location.pathname === '/login'
  const isSignupPage = location.pathname === '/signup'
  const navLinks = [
    { label: marketingCopy.public_v3_nav_why_label || 'Why Ricald', href: '/#problem' },
    { label: marketingCopy.public_v3_nav_how_label || 'How it works', href: '/#mechanism' },
    { label: marketingCopy.public_v3_nav_pricing_label || 'Pricing', href: '/pricing' },
    { label: 'Download', href: '/download' },
    { label: marketingCopy.public_v3_nav_contact_label || 'Contact', href: '/contact' },
  ]
  const startCta = marketingCopy.public_v3_nav_cta || 'Start with one objective'
  const signInLabel = marketingCopy.public_v3_nav_sign_in || 'Sign in'

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20)
    }
    handleScroll()
    window.addEventListener('scroll', handleScroll)
    return () => window.removeEventListener('scroll', handleScroll)
  }, [])

  return (
    <header
      className="fixed top-0 left-0 right-0 z-50 transition-all duration-300 backdrop-blur-xl"
      style={
        isScrolled
          ? {
              background: 'var(--marketing-navbar-bg-scrolled)',
              borderBottom: '1px solid var(--border)',
              boxShadow: '0 12px 30px rgba(26, 26, 24, 0.06)',
            }
          : {
              background: 'transparent',
              borderBottom: '1px solid transparent',
            }
      }
    >
      <div className="layout-container">
        <nav className="flex items-center justify-between h-16 md:h-20">
          {/* Logo */}
          <a
            href="/"
            className="flex items-center"
          >
            <img src={ricaldLogo} alt="Ricald" style={{ height: '54px', width: 'auto' }} />
          </a>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center gap-8">
            {navLinks.map((link) => (
              <a
                key={link.label}
                href={link.href}
                className={`text-sm font-medium transition-colors ${isScrolled ? 'text-[var(--text-secondary)] hover:text-[var(--text-primary)]' : 'text-[var(--text-secondary)] hover:text-[var(--text-primary)]'}`}
              >
                {link.label}
              </a>
            ))}
          </div>

          {/* Desktop Actions */}
          <div className="hidden md:flex items-center gap-4">
            {!isLoginPage && (
              <a
                href="/login"
                className={`text-sm font-medium transition-colors ${isScrolled ? 'text-[var(--text-secondary)] hover:text-[var(--text-primary)]' : 'text-[var(--text-secondary)] hover:text-[var(--text-primary)]'}`}
              >
                {signInLabel}
              </a>
            )}
            {!isSignupPage && (
              <a
                href="/signup"
                className={`${buildButtonClasses({ variant: 'primary', size: 'sm' })} shadow-[0_0_20px_rgba(var(--accent-glow-rgba),0.3)]`}
              >
                {startCta}
              </a>
            )}
          </div>

          {/* Mobile menu button */}
          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className="md:hidden flex h-9 w-9 items-center justify-center rounded-lg border border-[var(--border)] bg-[var(--bg-card)]"
            aria-label="Toggle menu"
          >
            <svg
              width="20"
              height="20"
              className="w-5 h-5 min-w-[20px] text-[var(--text-primary)]"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              {isMobileMenuOpen ? (
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              ) : (
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
              )}
            </svg>
          </button>
        </nav>

        {/* Mobile menu */}
        {isMobileMenuOpen && (
          <div className="md:hidden absolute top-full left-0 right-0 bg-[var(--bg-card)] border-b border-[var(--border)] shadow-lg">
            <div className="layout-container py-4 space-y-4">
              {navLinks.map((link) => (
                <a
                  key={link.label}
                  href={link.href}
                  onClick={() => setIsMobileMenuOpen(false)}
                  className="block py-2 text-[var(--text-secondary)] hover:text-[var(--text-primary)]"
                >
                  {link.label}
                </a>
              ))}
              <div className="pt-4 border-t border-[var(--border)] space-y-3">
                {!isLoginPage && (
                  <a
                    href="/login"
                    className="block py-2 text-[var(--text-secondary)]"
                  >
                    {signInLabel}
                  </a>
                )}
                {!isSignupPage && (
                  <a
                    href="/signup"
                    className={`${buildButtonClasses({ variant: 'primary', fullWidth: true })}`}
                  >
                    {startCta}
                  </a>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </header>
  )
}

export default ModernNavbar
